import { firestoreAction } from 'vuexfire'
import * as firebase from 'firebase'
import db from '@/db'
import wilson from 'wilson-interval'
import { setTimeout } from 'timers'

const estimatesRef = db.collection('estimates')
const orgRef = db.collection('organization')
const ratesRef = db.collection('rates')
const updated = firebase.default.firestore.FieldValue.serverTimestamp()

const initialState = {
  estimates: [],
  estimatesLoading: true
}

const state = Object.assign({}, initialState)

const mutations = {
  'SET_ESTIMATES_LOADING' (state, loading) {
    state.estimatesLoading = loading
  },
  'RESET_STATE' (state) {
    for (let prop in state) {
      state[prop] = initialState[prop]
    }
  }
}

const actions = {
  resetEstimateState: firestoreAction(({ commit, unbindFirestoreRef }) => {
    unbindFirestoreRef('estimates')
    commit('RESET_STATE')
  }),
  setEstimatesRef: firestoreAction(({ bindFirestoreRef, commit, dispatch }, ref) => {
    bindFirestoreRef('estimates', ref).then(response => {
      commit('SET_ESTIMATES_LOADING', false)
      dispatch('toggleLoading', false)
    }).catch(error => {
      console.log(error)
    })
  }),
  addEstimate: ({ commit, rootGetters, dispatch }, payload) => {
    const estimate = { ...payload }
    if (estimate.rateCard !== 'zzzzz') {
      estimate.rateValue = 0
      const rateCard = rootGetters.rateUnits(estimate.rateCard)
      estimate.rateValue = rateCard[estimate.rateType].amount
    }
    return new Promise((resolve, reject) => {
      estimatesRef.add(estimate).then(entry => {
        // Build reference for other collections
        const id = entry.id
        let estimateInfo = {
          updated: updated
        }
        const estimateKey = 'estimates.' + id
        estimateInfo[estimateKey] = true

        // Write batch reference to other collections
        const batch = db.batch()
        const org = orgRef.doc(estimate.organization)
        batch.update(org, estimateInfo)
        if (estimate.rateCard !== 'zzzzz') {
          const rate = ratesRef.doc(estimate.rateCard)
          batch.update(rate, estimateInfo)
        }

        // Commit updates
        batch.commit().then(response => {
          dispatch('saveState', true)
        }).catch(error => {
          console.log(error)
        })
        resolve(id)
      }, error => {
        reject(error)
      })
    })
  },
  archiveEstimate: ({ state, dispatch }, id) => {
    const update = {
      updated: updated,
      archived: updated
    }
    estimatesRef.doc(id).update(update).then(response => {
      dispatch('saveState', true)
    }).catch(error => {
      console.log(error)
    })
  },
  estimateSettings: ({ rootGetters, dispatch }, payload) => {
    const estimate = { ...payload }
    const batch = db.batch()
    estimate['updated'] = updated
    const estimateDoc = estimatesRef.doc(estimate.id)
    batch.update(estimateDoc, estimate)

    const estimateKey = 'estimates.' + estimate.id
    const estimateInfo = {
      updated: updated
    }
    !payload.rateCard ? estimate.rateCard = 'zzzzz' : estimate.rateCard = payload.rateCard
    if (estimate.rateCard !== 'zzzzz') {
      estimateInfo[estimateKey] = true
      const rateDoc = ratesRef.doc(estimate.rateCard)
      batch.update(rateDoc, estimateInfo)
      const rateCard = rootGetters.rateUnits(estimate.rateCard)
      estimate.rateValue = rateCard[estimate.rateType].amount
    }
    if (estimate.lastRateCard !== estimate.rateCard) {
      if (estimate.lastRateCard) {
        const rateDoc = ratesRef.doc(estimate.lastRateCard)
        const rateObject = 'estimates.' + estimate.id
        const rateInfo = {}
        rateInfo[rateObject] = firebase.default.firestore.FieldValue.delete()
        batch.update(rateDoc, rateInfo)
        batch.update(rateDoc, { updated: updated })
      }
    }
    batch.commit().then(response => {
      dispatch('calculateBudget', estimate.id)
    }).catch(error => {
      console.log(error)
    })
  },
  calculateBudget: ({ getters, rootGetters, commit, dispatch }, key) => {
    // Grab the estimate
    const estimate = getters.estimate(key)
    // Set rates
    // Use the rateValue field by default
    const rateValue = estimate.rateValue

    // Set task totals
    // Grab all the tasks in this estimate
    const tasks = rootGetters.tasks(key)
    // Initialize lows and highs at 0
    let lowTime = 0
    let highTime = 0
    // Check if any tasks even exist
    if (tasks) {
      // Create low and high arrays to capture all the values
      let lows = []
      let highs = []
      // Push the lows and highs of each task into their arrays
      tasks.forEach(task => {
        lows.push(task.lowEstimate)
        highs.push(task.highEstimate)
      })
      // Add up all the values in each of the low and high arrays
      lowTime = lows.reduce((a, b) => a + b, 0)
      highTime = highs.reduce((a, b) => a + b, 0)
    }

    // Initialize the number of red Flags starting at 0
    let numRedFlags = 0
    // Create the red flag map of this estimate
    let rfMap = {}
    // If there are red flags selected, count how many there are and create the rfMap object
    if (estimate.redFlags && Object.keys(estimate.redFlags).length) {
      numRedFlags = Object.keys(estimate.redFlags).length
    }

    // Set role breakdown
    // Create roles object
    const roles = {}
    // Make array of unique roles among tasks
    const uniqueRoles = [...new Set(tasks.map(item => item.role))]
    // Count the total number of unique roles
    let numRoles = uniqueRoles.length
    // Run through each role used on this estimate
    uniqueRoles.forEach(unique => {
      // Grab the tasks that are assigned this role
      const roleTasks = rootGetters.taskRole(estimate.id, unique)
      // Create low and high arrays for this role's tasks
      let roleLows = []
      let roleHighs = []
      roleTasks.forEach(task => {
        roleLows.push(task.lowEstimate)
        roleHighs.push(task.highEstimate)
      })
      // Add up the low and high time estimates
      let roleLowTime = roleLows.reduce((a, b) => a + b, 0)
      let roleHighTime = roleHighs.reduce((a, b) => a + b, 0)
      // Find the difference between role highs and lows
      const timeDifference = roleHighTime - roleLowTime
      // Figure out the high and low prices
      let roleLowPrice = roleLowTime * rateValue
      let roleHighPrice = roleHighTime * rateValue
      // Set the mid-time to equal the low time by default
      let roleMidTime = roleLowTime

      // Assign an object to this role on the rfMap
      rfMap[unique] = {}
      // Set the modifier and red flag total to 0 by default
      rfMap[unique].modifier = 0
      rfMap[unique].rfTotal = 0

      if (unique && unique !== 'zzzzz') {
        // Calculate red flags if there are any
        if (estimate.redFlags && Object.keys(estimate.redFlags).length) {
          // Grab all the red flags in the estimate and copy the object
          const redFlags = { ...estimate.redFlags }
          Object.keys(redFlags).forEach(flag => {
            // Find the red flag entry
            const redFlag = { ...rootGetters.redFlag(flag) }
            // For each red flag, total up all the impacts of this role
            rfMap[unique].rfTotal += redFlag.roles[unique].impact
          })
          // If this role's redflag total is great than 0, figure out the modifier
          if (rfMap[unique].rfTotal > 0) {
            // Calculate the modifier by dividing the red flag total impacts on this role by the max impacts available
            const observed = rfMap[unique].rfTotal
            const population = numRedFlags * 5
            const wilsonModifier = wilson(observed, population)
            rfMap[unique].modifier = wilsonModifier.low
            // The middle time = lot time + ((high role time - low role time) * role modifier)
            roleMidTime = roleLowTime + (timeDifference * wilsonModifier.low)
          }
        }
      }
      const roleMidPrice = roleMidTime * rateValue

      const role = rootGetters.role(unique)
      roles[unique] = {
        name: !role ? 'Unassigned' : role.name,
        modifier: !role ? 1 : rfMap[unique].modifier,
        lowTime: roleLowTime,
        midTime: roleMidTime,
        highTime: roleHighTime,
        lowPrice: roleLowPrice,
        midPrice: roleMidPrice,
        highPrice: roleHighPrice
      }
    })

    // Set team breakdown
    const team = {}
    // Make array of unique people among tasks
    const uniquePeople = [...new Set(tasks.map(item => item.person))]
    // Calculate each role
    uniquePeople.forEach(unique => {
      const personTasks = rootGetters.taskPerson(estimate.id, unique)
      let personLows = []
      let personHighs = []
      personTasks.forEach(task => {
        personLows.push(task.lowEstimate)
        personHighs.push(task.highEstimate)
      })
      const personLowTime = personLows.reduce((a, b) => a + b, 0)
      const personHighTime = personHighs.reduce((a, b) => a + b, 0)
      const personLowPrice = personLowTime * rateValue
      const personHighPrice = personHighTime * rateValue
      const personTimeDifference = personHighTime - personLowTime
      let personMidTime = personLowTime
      let modifiers = 0
      if (estimate.redFlags && Object.keys(estimate.redFlags).length && unique !== 'zzzzz') {
        const roles = personTasks.map(task => [ task.role ])
        roles.forEach(role => {
          if (rfMap[role].modifier) {
            modifiers += (rfMap[role].modifier * 100) / 100
          }
        })
        // const personModifier = wilson(modifiers, roles.length)
        const personModifier = modifiers / roles.length
        personMidTime = personLowTime + (personTimeDifference * personModifier)
      }
      const personMidPrice = personMidTime * rateValue

      // Set the Person's object on the estimate
      const person = rootGetters.person(unique)
      team[unique] = {
        name: !person ? 'Unassigned' : person.fullName,
        lowTime: personLowTime,
        midTime: personMidTime,
        highTime: personHighTime,
        lowPrice: personLowPrice,
        midPrice: personMidPrice,
        highPrice: personHighPrice
      }
    })
    // Set the midTime = lowTime by default
    let midTime = lowTime
    // If there is more than one role used, add up the mids in them
    if (numRoles > 0) {
      let mids = []
      // Make an array of mids
      Object.keys(roles).forEach(role => {
        mids.push(roles[role].midTime)
      })
      // Add up the mids array
      midTime = mids.reduce((a, b) => a + b, 0)
    }
    // Set default range numbers
    let lowMidTime = lowTime
    // Calculate the lower end of the range if the mid value is higher than the low
    if (midTime > lowTime) {
      lowMidTime = (((midTime - lowTime) / 3) * 2) + lowTime
    }
    // Calculate the higher range if mid Time is lower than high
    let midHighTime = highTime
    if (midTime < highTime) {
      midHighTime = ((highTime - midTime) / 3) + midTime
    }
    const lowLabour = lowTime * estimate.projectManagement
    const midLabour = midTime * estimate.projectManagement
    const highLabour = highTime * estimate.projectManagement
    const lowMidLabour = lowMidTime * estimate.projectManagement
    const midHighLabour = midHighTime * estimate.projectManagement
    const lowPrice = lowLabour * rateValue
    const lowMidPrice = lowMidLabour * rateValue
    const midPrice = midLabour * rateValue
    const midHighPrice = midHighLabour * rateValue
    const highPrice = highLabour * rateValue

    // Set new rate parameters
    const update = {}
    update.lowTime = lowTime
    update.lowMidTime = lowMidTime
    update.midTime = midTime
    update.midHighTime = midHighTime
    update.highTime = highTime
    update.roles = roles
    update.team = team
    update.rateValue = rateValue
    update.lowPrice = lowPrice
    update.lowMidPrice = lowMidPrice
    update.midPrice = midPrice
    update.midHighPrice = midHighPrice
    update.highPrice = highPrice
    update['updated'] = updated
    estimatesRef.doc(key).update(update).then(response => {
      dispatch('saveState', true)
    })
  },
  duplicateEstimate: ({ dispatch }, payload) => {
    const estimateData = { ...payload }
    const oldEstimate = estimateData.id
    delete estimateData.id
    estimateData.project = payload.project + ' copy'
    estimateData.updated = updated
    estimateData.created = updated
    const taskList = estimateData.tasks
    estimateData.tasks = false
    return new Promise((resolve, reject) => {
      estimatesRef.add(estimateData).then(entry => {
        const id = entry.id
        let estimateInfo = {
          updated: updated
        }
        const estimateKey = 'estimates.' + id
        estimateInfo[estimateKey] = true
        orgRef.doc(estimateData.organization).update(estimateInfo).then(response => {
          const newInfo = {
            oldEstimate: oldEstimate,
            newEstimate: id,
            organization: estimateData.organization,
            owner: estimateData.owner,
            tasks: taskList
          }
          dispatch('duplicateTasks', newInfo).then(response => {
            resolve(id)
            setTimeout(delay => {
              dispatch('saveState', true)
              dispatch('toggleLoading', false)
            }, 2000)
          }).catch(error => {
            console.log(error)
            dispatch('saveState', true)
            dispatch('toggleLoading', false)
          })
        }).catch(error => {
          console.log(error)
          dispatch('saveState', true)
          dispatch('toggleLoading', false)
        })
      }, error => {
        reject(error)
        dispatch('saveState', true)
        dispatch('toggleLoading', false)
      })
    })
  },
  duplicateEstimates: ({ dispatch }, payload) => {
    return new Promise((resolve, reject) => {
      const oldEstimates = { ...state.estimates }
      const estimateMatrix = {}
      const batch = db.batch()
      let estimateCount = 0
      Object.keys(oldEstimates).forEach(estimate => {
        const newEstimate = { ...oldEstimates[estimate] }
        const oldEstimate = oldEstimates[estimate].id
        delete newEstimate.id
        newEstimate.owner = payload.user
        newEstimate.created = updated
        newEstimate.updated = updated
        newEstimate.organization = payload.org
        newEstimate.tasks = false
        if (newEstimate.lastRateCard) {
          newEstimate.lastRateCard = payload.rates[newEstimate.lastRateCard]
        } else {
          newEstimate.lastRateCard = false
        }
        if (newEstimate.rateCard) {
          newEstimate.rateCard = payload.rates[newEstimate.rateCard]
        } else {
          newEstimate.rateCard = false
        }
        if (newEstimate.roles) {
          const newRoles = {}
          Object.keys(newEstimate.roles).forEach(role => {
            const key = payload.roles[role]
            const numbers = { ...newEstimate.roles[role] }
            newRoles[key] = numbers
          })
          newEstimate.roles = newRoles
        } else {
          newEstimate.roles = false
        }
        if (newEstimate.team) {
          const newTeam = {}
          Object.keys(newEstimate.team).forEach(person => {
            const key = payload.team[person]
            const numbers = { ...newEstimate.team[person] }
            newTeam[key] = numbers
          })
          newEstimate.team = newTeam
        } else {
          newEstimate.team = false
        }
        if (newEstimate.redFlags) {
          const newFlags = {}
          Object.keys(newEstimate.redFlags).forEach(flag => {
            const key = payload.redFlags[flag]
            newFlags[key] = true
          })
          newEstimate.redFlags = newFlags
        } else {
          newEstimate.redFlags = false
        }
        estimatesRef.add(newEstimate).then(reference => {
          estimateMatrix[oldEstimate] = reference.id
          const org = orgRef.doc(payload.org)
          const orgKey = 'estimates.' + reference.id
          const orgInfo = {}
          orgInfo[orgKey] = true
          if (newEstimate.rateCard) {
            const rate = ratesRef.doc(newEstimate.rateCard)
            batch.update(rate, orgInfo)
          }
          batch.update(org, orgInfo)
          estimateCount++
          if (estimateCount === state.estimates.length) {
            commitBatch()
          }
        }).catch(error => {
          console.log(error)
        })
      })
      function commitBatch () {
        batch.commit().then(response => {
          resolve(estimateMatrix)
        }, error => {
          reject(error)
        })
      }
    })
  }
}

const getters = {
  estimatesLoading: state => {
    return state.estimatesLoading
  },
  estimates: (state, rootGetters) => {
    return state.estimates.filter(estimates => estimates.archived === false)
  },
  estimate: state => (key) => {
    return state.estimates.find(estimates => estimates.id === key)
  },
  estimateSettings: state => (key) => {
    const estimate = state.estimates.find(estimates => estimates.id === key)
    const pm = Math.round((estimate.projectManagement - 1) * 100)
    const rate = Math.round(estimate.rateValue)
    return {
      projectManagement: pm,
      rate: rate
    }
  },
  estimateCsv: (state, rootGetters) => (key) => {
    const csv = []
    const estimate = state.estimates.find(estimates => estimates.id === key)
    const rateId = estimate.rateType
    const units = rootGetters.rateUnits('zzzzz')
    const unit = units[rateId].label
    const tasks = rootGetters.tasks(key)
    tasks.forEach(task => {
      let roleName, personName
      console.log(task.role)
      if (task.role === 'zzzzz' || !task.role) {
        roleName = 'Unassigned'
      } else {
        const role = rootGetters.role(task.role)
        roleName = role.name
      }
      if (task.person === 'zzzzz' || !task.person) {
        personName = 'Unassigned'
      } else {
        const person = rootGetters.person(task.person)
        personName = person.fullName
      }
      const row = {
        'Name': task.name,
        'Role': roleName,
        'Person': personName
      }
      row['Likely ' + unit + 's'] = task.lowEstimate
      row['High ' + unit + 's'] = task.highEstimate
      csv.push(row)
    })
    function convertToCsv (args) {
      let result, ctr, keys, columnDelimiter, lineDelimiter, data
      data = args.data || null
      if (data == null || !data.length) {
        return null
      }
      columnDelimiter = args.columnDelimiter || ','
      lineDelimiter = args.lineDelimiter || '\n'
      keys = Object.keys(data[0])
      result = ''
      result += keys.join(columnDelimiter)
      result += lineDelimiter
      data.forEach(item => {
        ctr = 0
        keys.forEach(key => {
          if (ctr > 0) result += columnDelimiter
          result += item[key]
          ctr++
        })
        result += lineDelimiter
      })
      return result
    }
    return convertToCsv({
      data: csv
    })
  },
  redFlagDistribution: (state, rootGetters) => (key) => {
    const estimate = state.estimates.find(estimates => estimates.id === key)
    const accent = rootGetters.organization.theme.accent
    let labels = []
    let data = []
    if (estimate.roles) {
      Object.keys(estimate.roles).forEach(role => {
        labels.push(estimate.roles[role].name)
        data.push(Math.round(estimate.roles[role].modifier * 100))
      })
    }
    return {
      labels: labels,
      datasets: [{
        data: data,
        borderColor: accent,
        backgroundColor: accent
      }]
    }
  }
}

export default {
  state,
  mutations,
  actions,
  getters
}
